/**
 * Easter phone wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterPhoneWallpapersPack: ToilePack = {
    slug: 'easter-phone-wallpapers',
    category: 'season',
    name: 'Easter phone wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bunny', label: 'Bunny in tulips', subject: 'A small bunny sitting among tulips at the bottom of the frame, a calm pale sky above'},
        {key: 'eggs', label: 'Eggs on grass', subject: 'A row of painted eggs on spring grass at the bottom of the frame, a calm sky above'},
        {key: 'blossom', label: 'Blossom branch', subject: 'A blossom branch reaching in from one lower corner, a calm pale ground above'},
    ],
    styles: [
        {key: 'cute', label: 'Cute', style: 'cute flat illustration, rounded shapes, soft and quiet', palette: 'Pastel pink, mint and butter yellow', framing: 'The focal point in the lower third and the upper half calm for the clock and icons'},
        {key: 'painted', label: 'Painted', style: 'soft watercolour painting, blossom and light', palette: 'Blossom pink, spring green and pale sky', framing: 'The focal point low, the sky calm above for widgets'},
        {key: 'minimal', label: 'Minimal', style: 'minimal flat illustration, few shapes, lots of calm ground', palette: 'Sage green and cream with a single yellow', framing: 'A quiet center for icons, the motif at the edges'},
    ],
    directions: ['a chick instead', 'in a brighter color palette', 'for a tablet screen'],
    directionHint: 'try: a chick instead',
    seo: {
        title: 'Easter phone wallpapers: bunny, eggs, blossom',
        metaDescription: 'Nine Easter lock screen wallpapers — a bunny in tulips, eggs on grass, a blossom branch — in cute, painted or minimal style, calm top for icons.',
        h1: 'Easter phone wallpaper: bunny, painted eggs, blossom branch',
        tagline: 'Nine wallpapers, three spring scenes, three styles each — the top stays calm so the clock and icons sit clean.',
        intro: "A lock screen has to leave room for the clock — the top can't be busy. These nine set a bunny sitting among tulips, a row of painted eggs on spring grass, and a blossom branch reaching into the frame, each as cute, painted or minimal. Ask for a different scene, like a chick, and the same calm top carries it.",
        useCases: [
            'an iPhone or Android lock screen',
            'a pastel spring phone background',
            'a calm background behind widgets and the clock',
            'a matching wallpaper across phone and tablet',
        ],
        faq: [
            {q: 'Will the clock and icons stay readable over the wallpaper?', a: 'Yes — every wallpaper keeps its top calm and low-contrast, so the clock, notifications and app icons stay legible over it.'},
            {q: 'What size do these render at?', a: 'Portrait phone proportions, composed so the focal point sits low and the top third stays quiet.'},
            {q: 'Can I request a different scene, like a chick?', a: 'Yes — the direction field takes any spring scene, and the same three styles render it with a calm top.'},
            {q: "What's the difference between cute, painted and minimal?", a: 'Cute is a soft rounded flat illustration, painted is a watercolour of blossom and light, and minimal is a flat few-shape illustration.'},
        ],
    },
}
